import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import moment from "moment-timezone";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import { toast } from "react-toastify";

import ai from "../../helpers/axios";
import currencyFormatter from "../../helpers/currencyFormatter";
import CompChargeModal from "../payments-linking/comp-charge-modal";
import PaymentUnlinkModal from "../payments-linking/payment-unlink-modal";
import TransferIncidentalToLedgerModal from "./transfer-incidental-to-ledger-modal";
import UnlinkIncidentalLedgerModal from "./unlink-incidental-ledger-modal";
import MoveIncidentalFundsModal from "./move-incidental-funds-modal";
import RevertIncidentalFundsModal from "./revert-incidental-funds-modal";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const NEEDS_ACTION_COLOR = "#ffe0a3";

const TransferMenu = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const handleSelect = (event, option) => {
    handleClose(event);
    onSelect(option);
  };

  return (
    <>
      <button
        ref={anchorRef}
        className="base-button icon-button icon-button-small evo-blue ml-2"
        onClick={handleToggle}
        title="Transfer Money"
        type="button"
      >
        <CurrencyExchangeIcon fontSize="small"/>
      </button>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" transition sx={{ zIndex: 1 }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} sx={{ p: 0 }}>
                  <MenuItem onClick={(event) => handleSelect(event, "ledger")}>To/From Funds Ledger</MenuItem>
                  <MenuItem onClick={(event) => handleSelect(event, "trip")}>To another Trip</MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

const IncidentalChargeRow = ({ row, canComp, canUnlink, onComp, onUnlink }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = useCallback((event) => {
    event.stopPropagation();
    setIsOpen((prevOpen) => !prevOpen);
  }, []);

  const isPositive = (row.amount || 0) >= 0;
  const isExpandable = row.source === "amex" || row.source === "stripe";

  return (
    <>
      <tr
        className="fm-l2-parent-table-row"
        style={{ backgroundColor: row.source === "amex" && !row.actionType ? NEEDS_ACTION_COLOR : "" }}
      >
        <td>
          {isExpandable && (
            <IconButton aria-label="expand row" size="small" onClick={toggleOpen} className="p-0">
              {isOpen ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
            </IconButton>
          )}
        </td>
        <td>{row.date ? moment(row.date).format("M/D/YY") : "-"}</td>
        <td>
          {row.source === "fundsLedger"
            ? <Link to="/funds-ledger" target="_blank" rel="noopener noreferrer">{row.sourceLabel}</Link>
            : (row.sourceLabel || "-")}
        </td>
        <td>{row.categoryLabel || "-"}</td>
        <td>
          <Tooltip title={row.notes || ""}>
            <div className="two-line-content">{row.notes || "-"}</div>
          </Tooltip>
        </td>
        <td className={`text-align-right amount-${isPositive ? "positive" : "negative"}`}>
          {currencyFormatter.format(row.amount || 0)}
        </td>
        <td className="text-align-right">
          {row.source === "stripe" || row.source === "fundsLedger" || row.source === "clients"
            ? "n/a"
            : row.actionType
              ? currencyFormatter.format(row.amountToComp || 0)
              : "-"}
        </td>
        <td>
          <div className="d-flex justify-content-end">
            {canComp && (
              <button
                className="base-button icon-button icon-button-small evo-blue"
                onClick={() => onComp(row)}
                title="Comp"
                type="button"
              >
                <TrendingDownIcon fontSize="small"/>
              </button>
            )}

            {canUnlink && (
              <button
                className="base-button icon-button icon-button-small btn-danger ml-2"
                onClick={() => onUnlink(row)}
                title="Unlink"
                type="button"
              >
                <LinkOffIcon fontSize="small"/>
              </button>
            )}
          </div>
        </td>
      </tr>

      <tr className={`payment-records-table-row-expandable ${isOpen ? "payment-records-table-row-expanded" : ""}`}>
        <td colSpan={8} className={isOpen ? "" : "p-0"}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            {row.source === "amex" ? (
              <div className="d-flex gap-20">
                <div><b>Amount to Charge:</b> {row.amountToCharge != null ? currencyFormatter.format(row.amountToCharge) : "-"}</div>
                <div><b>Amount to Comp:</b> {row.amountToComp != null ? currencyFormatter.format(row.amountToComp) : "-"}</div>
              </div>
            ) : row.source === "stripe" ? (
              <div className="d-flex gap-20">
                <div><b>Stripe Fee:</b> {currencyFormatter.format(row.feeAmount || 0)}</div>
                <div><b>Gross Amount:</b> {currencyFormatter.format(row.grossAmount || 0)}</div>
              </div>
            ) : null}
          </Collapse>
        </td>
      </tr>
    </>
  );
};

const IncidentalChargesModule = (props) => {
  const { tripId, tripTag, dataDownloadedTimestamp, readOnly, superAdmin, isAccountingAdmin, isAccountingLedger, isTripOwner, onFundsChanged } = props;

  const { getAccessTokenSilently } = useAuth0();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tripSummary, setTripSummary] = useState(null);
  const [compModalState, setCompModalState] = useState({ open: false, row: null });
  const [unlinkModalState, setUnlinkModalState] = useState({ open: false, payment: null });
  const [transferModalState, setTransferModalState] = useState({ open: false, saving: false });
  const [unlinkLedgerModalState, setUnlinkLedgerModalState] = useState({ open: false, row: null, saving: false });
  const [moveFundsModalState, setMoveFundsModalState] = useState({ open: false, saving: false });
  const [revertFundsModalState, setRevertFundsModalState] = useState({
    open: false,
    saving: false,
    transferGroupId: null,
    sourceInfo: null,
    targetInfo: null,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const token = await getAccessTokenSilently();
      const { data: rows } = await ai.auth(token).get(`/api/trips/${tripId}/incidental-charges`);
      setData(rows || []);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, tripId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, dataDownloadedTimestamp]);

  // The Comp Charge modal's read-only Trip field (Mode 2) needs departure date,
  // routing, and client name — this module only carries tripTag, so fetch the rest
  // once via the same /trips/search shape the modal already trusts for Mode 1.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data: results } = await ai.auth(token).get("/api/trips/search", {
          params: { q: tripTag, limit: 5 },
        });
        const match = (results?.data || []).find((t) => t.tripTag === tripTag) || null;
        if (!cancelled) setTripSummary(match);
      } catch {
        // Best-effort enrichment only — the modal still opens, just with a thinner Trip label.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, tripTag]);

  const netToFirm = useMemo(
    () => data.reduce((sum, row) => sum + (row.amount || 0), 0),
    [data],
  );

  const totalComps = useMemo(
    () => data.reduce((sum, row) => sum + (row.actionType === "comp" ? (row.amountToComp || 0) : 0), 0),
    [data],
  );

  const handleOpenComp = useCallback((row) => {
    setCompModalState({
      open: true,
      row: {
        _id: row.xeroTransactionId,
        _lineItemID: row.lineItemID,
        transactionDate: row.date,
        cardName: row.sourceLabel,
        contactName: row.contactName,
        categoryLabel: row.categoryLabel,
        reference: row.reference,
        amount: row.amount,
        trip: {
          tripTag,
          firstDepartureDate: tripSummary?.firstDepartureDate,
          lastArrivalDate: tripSummary?.lastArrivalDate,
          routing: tripSummary?.routing,
        },
        clientOrOperator: tripSummary?.clientName,
      },
    });
  }, [tripTag, tripSummary]);

  const handleCloseComp = useCallback(async (refresh = true) => {
    setCompModalState({ open: false, row: null });

    if (refresh) {
      await fetchData();
    }
  }, [fetchData]);

  const handleOpenUnlink = useCallback(async (row) => {
    if (row.source === "fundsLedger") {
      if (row.transferGroupId) {
        const token = await getAccessTokenSilently();
        const { data } = await ai.auth(token).get(
          `/api/trips/${tripId}/incidental-funds-movement/${row.transferGroupId}/info`,
        );
        setRevertFundsModalState({
          open: true,
          saving: false,
          transferGroupId: row.transferGroupId,
          sourceInfo: data.source,
          targetInfo: data.target,
        });
        return;
      }

      setUnlinkLedgerModalState({ open: true, row, saving: false });
      return;
    }

    const payment = row.source === "amex" || row.source === "clients"
      ? {
        _id: row.xeroTransactionId,
        _lineItemID: row.lineItemID,
        targetingPayments: row.source,
        incidentalChargeId: row._id,
        // Trip L2's own Unlink CTA is Super Admin / Accounting Admin only, no owner
        // carve-out (EV-1886) — stricter than the Amex tab's own Unlink control (EV-1993),
        // which shares this same server endpoint. Server enforces per this flag.
        strict: true,
      }
      : {
        _id: row.stripeChargeId,
        chargeId: row.stripeChargeId,
        incidentalChargeId: row._id,
      };

    setUnlinkModalState({ open: true, payment });
  }, [getAccessTokenSilently, tripId]);

  const handleCloseUnlink = useCallback(async (refresh = true) => {
    setUnlinkModalState({ open: false, payment: null });

    if (refresh) {
      await fetchData();
    }
  }, [fetchData]);

  const handleOpenTransfer = useCallback(() => {
    setTransferModalState({ open: true, saving: false });
  }, []);

  const handleCloseTransfer = useCallback(() => {
    setTransferModalState({ open: false, saving: false });
  }, []);

  const handleTransferSubmit = useCallback(async (formData) => {
    setTransferModalState((s) => ({ ...s, saving: true }));

    try {
      const token = await getAccessTokenSilently();
      await ai.auth(token).post(`/api/trips/${tripId}/transfer-incidental-to-ledger`, formData);
      setTransferModalState({ open: false, saving: false });
      await fetchData();
      onFundsChanged?.();
      toast.success("Transfer completed successfully.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Error completing transfer"));
      setTransferModalState((s) => ({ ...s, saving: false }));
    }
  }, [getAccessTokenSilently, tripId, fetchData, onFundsChanged]);

  const handleCloseUnlinkLedger = useCallback(() => {
    setUnlinkLedgerModalState({ open: false, row: null, saving: false });
  }, []);

  const handleUnlinkLedgerSubmit = useCallback(async () => {
    const row = unlinkLedgerModalState.row;
    if (!row) return;

    setUnlinkLedgerModalState((s) => ({ ...s, saving: true }));

    try {
      const token = await getAccessTokenSilently();
      await ai.auth(token).delete(`/api/trips/${tripId}/unlink-incidental-ledger-transfer/${row._id}`);
      setUnlinkLedgerModalState({ open: false, row: null, saving: false });
      await fetchData();
      onFundsChanged?.();
      toast.success("Payment unlinked successfully.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Error unlinking payment"));
      setUnlinkLedgerModalState((s) => ({ ...s, saving: false }));
    }
  }, [getAccessTokenSilently, tripId, unlinkLedgerModalState.row, fetchData, onFundsChanged]);

  const handleOpenMoveFunds = useCallback(() => {
    setMoveFundsModalState({ open: true, saving: false });
  }, []);

  const handleCloseMoveFunds = useCallback(() => {
    setMoveFundsModalState({ open: false, saving: false });
  }, []);

  const handleMoveFundsSubmit = useCallback(async (formData) => {
    setMoveFundsModalState((s) => ({ ...s, saving: true }));

    try {
      const token = await getAccessTokenSilently();
      await ai.auth(token).post(`/api/trips/${tripId}/move-incidental-funds`, formData);
      setMoveFundsModalState({ open: false, saving: false });
      await fetchData();
      onFundsChanged?.();
      toast.success("Funds moved successfully.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Error moving funds"));
      setMoveFundsModalState((s) => ({ ...s, saving: false }));
    }
  }, [getAccessTokenSilently, tripId, fetchData, onFundsChanged]);

  const handleCloseRevertFunds = useCallback(() => {
    setRevertFundsModalState({ open: false, saving: false, transferGroupId: null, sourceInfo: null, targetInfo: null });
  }, []);

  const handleRevertFundsSubmit = useCallback(async () => {
    const { transferGroupId } = revertFundsModalState;
    if (!transferGroupId) return;

    setRevertFundsModalState((s) => ({ ...s, saving: true }));

    try {
      const token = await getAccessTokenSilently();
      await ai.auth(token).delete(`/api/trips/${tripId}/incidental-funds-movement/${transferGroupId}/revert`);
      setRevertFundsModalState({ open: false, saving: false, transferGroupId: null, sourceInfo: null, targetInfo: null });
      await fetchData();
      onFundsChanged?.();
      toast.success("Funds movement reverted successfully.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Error reverting funds movement"));
      setRevertFundsModalState((s) => ({ ...s, saving: false }));
    }
  }, [getAccessTokenSilently, tripId, fetchData, revertFundsModalState, onFundsChanged]);

  const canUnlink = !readOnly && (superAdmin || isAccountingAdmin);
  const canTransfer = canUnlink && isAccountingLedger;

  return (
    <div className="trip-notes-module" id="trip-incidental-charges">
      <h5 className="mb-1 d-flex">
        Incidental Charges & Comps

        {canTransfer && (
          <TransferMenu
            onSelect={(option) => (option === "ledger" ? handleOpenTransfer() : handleOpenMoveFunds())}
          />
        )}
      </h5>

      <div className="trip-notes-table trip-incidental-charges-table pb-0">
        <table>
          <thead className="fm-l2-parent-table-head">
            <tr className="fm-l2-parent-table-head-row">
              <th/>
              <th>Date</th>
              <th>Source</th>
              <th>Category</th>
              <th>Notes</th>
              <th className="text-align-right">Amount</th>
              <th className="text-align-right">Comps</th>
              <th className="text-align-right">Actions</th>
            </tr>
          </thead>
          <tbody className="fm-l2-parent-table-body">
            {!loading && !data.length && (
              <tr className="fm-l2-parent-table-row">
                <td className="text-align-left" colSpan={8}>No incidental charges linked to this trip</td>
              </tr>
            )}

            {data.map((row) => (
              <IncidentalChargeRow
                key={row._id}
                row={row}
                canComp={!readOnly && row.source === "amex" && (!row.actionType || superAdmin || isAccountingAdmin || isTripOwner)}
                canUnlink={canUnlink && (row.source !== "fundsLedger" || isAccountingLedger)}
                onComp={handleOpenComp}
                onUnlink={handleOpenUnlink}
              />
            ))}

            {!!data.length && (
              <>
                <tr className="fm-l2-parent-table-row">
                  <td colSpan={5}><b>Net to Firm</b></td>
                  <td className="text-align-right"><b>{currencyFormatter.format(netToFirm)}</b></td>
                  <td/>
                  <td/>
                </tr>
                <tr className="fm-l2-parent-table-row">
                  <td colSpan={5}><b>Comps</b></td>
                  <td/>
                  <td className="text-align-right"><b>{currencyFormatter.format(totalComps)}</b></td>
                  <td/>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <CompChargeModal
        isOpen={compModalState.open}
        mode="comp"
        row={compModalState.row}
        onRequestClose={handleCloseComp}
      />

      <PaymentUnlinkModal
        payment={unlinkModalState.payment || {}}
        open={unlinkModalState.open}
        onRequestClose={handleCloseUnlink}
      />

      <TransferIncidentalToLedgerModal
        open={transferModalState.open}
        tripTag={tripTag}
        saving={transferModalState.saving}
        onSubmit={handleTransferSubmit}
        onClose={handleCloseTransfer}
      />

      <UnlinkIncidentalLedgerModal
        open={unlinkLedgerModalState.open}
        saving={unlinkLedgerModalState.saving}
        onSubmit={handleUnlinkLedgerSubmit}
        onClose={handleCloseUnlinkLedger}
      />

      <MoveIncidentalFundsModal
        open={moveFundsModalState.open}
        sourceTripId={tripId}
        sourceTripTag={tripTag}
        saving={moveFundsModalState.saving}
        onSubmit={handleMoveFundsSubmit}
        onClose={handleCloseMoveFunds}
      />

      <RevertIncidentalFundsModal
        open={revertFundsModalState.open}
        sourceInfo={revertFundsModalState.sourceInfo}
        targetInfo={revertFundsModalState.targetInfo}
        saving={revertFundsModalState.saving}
        onSubmit={handleRevertFundsSubmit}
        onClose={handleCloseRevertFunds}
      />
    </div>
  );
};

export default IncidentalChargesModule;
